/**
 * Hook to fetch team members for service projects
 * Người phụ trách = ai đang là assigned_user_id của ≥1 item trong dự án service (khớp khoá lọc ItemsManagement)
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TeamMember } from '@/hooks/useProjectTeamMembers';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export const useServiceTeamMembers = (projectIds: string[]) => {
  return useQuery({
    queryKey: ['service-team-members', [...projectIds].sort().join(',')],
    queryFn: async (): Promise<TeamMember[]> => {
      if (projectIds.length === 0) {
        return [];
      }

      // Step 1: Get assigned_user_id from items across ALL given projects (paginated — PostgREST cuts at 1000 rows/request without error)
      const ids = new Set<string>();
      const PAGE = 1000;
      for (let page = 0; page < 20; page++) {
        const q = supabase
          .from('items')
          .select('assigned_user_id')
          .in('project_id', projectIds)
          .not('assigned_user_id', 'is', null)
          .is('deleted_at', null)
          .range(page * PAGE, page * PAGE + PAGE - 1);
        const { data, error } = await q;
        if (error) throw error;
        (data || []).forEach(r => r.assigned_user_id && ids.add(r.assigned_user_id));
        if (!data || data.length < PAGE) break;
      }
      const userIds = [...ids];
      if (userIds.length === 0) {
        return [];
      }

      // Step 2: Get profiles for those user_ids (using chunked query)
      const profiles = await chunkedInQuerySimple<{ id: string; full_name: string | null; avatar_url: string | null; email: string | null }>(
        supabase,
        'profiles' as any,
        'id',
        userIds,
        'id, full_name, avatar_url, email'
      );

      // Transform to TeamMember structure
      const members: TeamMember[] = (profiles || [])
        .map((profile) => ({
          id: profile.id,
          full_name: profile.full_name || profile.email || 'Unknown',
          avatar_url: profile.avatar_url,
          email: profile.email || undefined,
        }))
        // Remove duplicates
        .filter((member, index, self) =>
          index === self.findIndex(m => m.id === member.id)
        )
        // Sort by name
        .sort((a, b) => a.full_name.localeCompare(b.full_name));

      return members;
    },
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,     // ✅ Match AUTH_QUERY_CONFIG
    placeholderData: keepPreviousData, // ✅ Smooth UX when data changes
  });
};
